import { Search, X } from "lucide-react";
import { isBaseballTradingCompetition } from "@sportex100/shared/utils/competitionIdentity.ts";
import type { StandingsMetricPill } from "@sportex100/shared/utils/playoffPicture.ts";
import type { MarketConferenceFilter, MarketDivisionFilter } from "@sportex100/shared/utils/marketStructureFilter.ts";
import { useLocalization, type TranslationKey } from "../lib/i18n";
import {
  recordPillTranslationKey,
  structureLabelKey,
  type RecordPill,
  type RegularSeasonStatusFilter,
} from "../lib/regularSeasonRecord";
import { SegmentedFilter } from "./ui";

type MarketTranslator = (
  key: TranslationKey,
  params?: Record<string, string | number>,
) => string;

const SEASON_PHASE_KEYS = {
  PRESEASON: "market.phase.preseason",
  REGULAR_SEASON: "market.phase.regularSeason",
  PLAYOFF_CUTOVER: "market.phase.playoffCutover",
  PLAYOFFS: "market.phase.playoffs",
  COMPLETED: "market.phase.completed",
} as const satisfies Record<string, TranslationKey>;

function readableUnknownCode(code: string): string {
  return code
    .trim()
    .replace(/[_-]+/g, " ")
    .toLocaleLowerCase()
    .replace(/\b\w/g, (letter) => letter.toLocaleUpperCase());
}

export function localizeSeasonPhase(
  phase: string | null | undefined,
  t: MarketTranslator,
): string {
  const normalized = (phase ?? "").trim().toUpperCase();
  const key = SEASON_PHASE_KEYS[normalized as keyof typeof SEASON_PHASE_KEYS];
  return key ? t(key) : readableUnknownCode(normalized);
}

export function localizeStructureLabel(code: string, t: MarketTranslator): string {
  const key = structureLabelKey(code);
  return key ? t(key) : readableUnknownCode(code);
}

export type RegularSeasonTopLevelKind = "league" | "conference";

export function regularSeasonTopLevelKind(
  competitionCode: string | null | undefined,
): RegularSeasonTopLevelKind {
  return isBaseballTradingCompetition(competitionCode) ? "league" : "conference";
}

export function RegularSeasonStructureHeadings({
  topLevelTitle,
  topLevelKind,
  divisionTitle,
}: {
  topLevelTitle?: string;
  topLevelKind: RegularSeasonTopLevelKind;
  divisionTitle: string;
}) {
  return (
    <>
      {topLevelTitle ? (
        <div className="regularSeasonTopLevelHeader overflow-x-auto border-b border-brand/40 pb-2 pt-2">
          <h2
            data-structure-kind={topLevelKind}
            className="whitespace-nowrap font-display text-xl font-bold uppercase tracking-[0.08em] text-brand"
          >
            {topLevelTitle}
          </h2>
        </div>
      ) : null}
      <h3
        data-structure-kind="division"
        className="regularSeasonDivisionHeader border-b border-border-subtle/60 pb-2 font-display text-base font-bold uppercase tracking-[0.08em] text-foreground-muted md:rounded-lg md:bg-background-elevated md:px-4 md:py-2 md:font-semibold md:tracking-wider md:text-foreground-secondary"
      >
        {divisionTitle}
      </h3>
    </>
  );
}

export function RegularSeasonRecordPills({
  teamCode,
  recordPills,
}: {
  teamCode: string;
  recordPills: RecordPill[];
}) {
  const { t } = useLocalization();
  if (recordPills.length === 0) return null;
  const recordAriaLabel = recordPills
    .map(({ label, value }) => t(recordPillTranslationKey(label), { value }))
    .join(", ");

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1" aria-label={recordAriaLabel}>
      {recordPills.map(({ label, value }) => (
        <span
          key={`${teamCode}-record-${label}`}
          className="inline-flex h-6 items-baseline gap-1 rounded border border-border-subtle bg-background px-1.5 font-mono text-[10px] font-semibold tabular-nums text-foreground-muted"
          aria-hidden="true"
        >
          <span className="text-foreground-faint">{label}</span>
          <span className="text-foreground">{value}</span>
        </span>
      ))}
    </div>
  );
}

const STANDINGS_METRIC_ACCESSIBILITY_KEYS = {
  PCT: "market.playoffPicture.metric.pctAccessibilityLabel",
  PTS: "market.playoffPicture.metric.ptsAccessibilityLabel",
  WCGB: "market.playoffPicture.metric.wcgbAccessibilityLabel",
  SEED: "market.playoffPicture.metric.seedAccessibilityLabel",
} as const satisfies Record<StandingsMetricPill["label"], TranslationKey>;

export function standingsMetricsAccessibilityLabel(
  metrics: StandingsMetricPill[],
  t: MarketTranslator,
): string {
  return metrics
    .map(({ label, displayValue }) => (
      `${t(STANDINGS_METRIC_ACCESSIBILITY_KEYS[label])} ${displayValue}`
    ))
    .join(", ");
}

export function RegularSeasonStandingsMetricPills({
  teamCode,
  metrics,
  translator,
}: {
  teamCode: string;
  metrics: StandingsMetricPill[];
  translator?: MarketTranslator;
}) {
  const { t: localizedT } = useLocalization();
  const t = translator ?? localizedT;
  if (metrics.length === 0) return null;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      {metrics.map(({ label, displayValue }) => (
        <span
          key={`${teamCode}-standings-${label}`}
          aria-label={standingsMetricsAccessibilityLabel([{ label, displayValue }], t)}
          className="inline-flex h-6 items-baseline gap-1 rounded border border-info/40 bg-info/10 px-1.5 font-mono text-[10px] font-semibold tabular-nums text-foreground-muted"
        >
          <span aria-hidden="true" className="text-info">{label}</span>
          <span aria-hidden="true" className="text-foreground">{displayValue}</span>
        </span>
      ))}
    </div>
  );
}

export function WildCardCutLine({
  translator,
}: {
  translator?: MarketTranslator;
}) {
  const { t: localizedT } = useLocalization();
  const t = translator ?? localizedT;

  return (
    <div
      role="separator"
      aria-label={t("market.playoffPicture.cutLineAccessibilityLabel")}
      className="flex items-center gap-2 py-1"
    >
      <span className="h-px flex-1 border-t border-dashed border-brand/55" aria-hidden="true" />
      <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-brand">
        {t("market.playoffPicture.cutLine")}
      </span>
      <span className="h-px flex-1 border-t border-dashed border-brand/55" aria-hidden="true" />
    </div>
  );
}

type RegularSeasonMarketFilterProps = {
  statusFilter?: RegularSeasonStatusFilter;
  searchQuery: string;
  visibleCount: number;
  totalCount: number;
  onStatusChange?: (value: RegularSeasonStatusFilter) => void;
  onSearchChange: (value: string) => void;
  showStatusFilter?: boolean;
  translator?: MarketTranslator;
  structure?: {
    conference: MarketConferenceFilter;
    division: MarketDivisionFilter;
    conferences: Exclude<MarketConferenceFilter, "ALL">[];
    divisions: Exclude<MarketDivisionFilter, "ALL">[];
    onConferenceChange: (value: MarketConferenceFilter) => void;
    onDivisionChange: (value: MarketDivisionFilter) => void;
  };
};

export function RegularSeasonMarketFilter({
  statusFilter,
  searchQuery,
  visibleCount,
  totalCount,
  onStatusChange,
  onSearchChange,
  showStatusFilter = false,
  translator,
  structure,
}: RegularSeasonMarketFilterProps) {
  const { t: localizedT } = useLocalization();
  const t = translator ?? localizedT;

  return (
    <section className="rounded-xl border border-border-subtle bg-background-card p-3 md:p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-faint"
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={t("market.search.placeholder")}
            className="h-10 w-full rounded-lg border border-border-subtle bg-background-raised pl-9 pr-16 text-sm text-foreground outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/30"
          />
          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
            <span className="rounded border border-border-subtle bg-background px-2 py-0.5 font-mono text-[11px] font-semibold tabular-nums text-foreground-muted">
              {visibleCount}/{totalCount}
            </span>
            {searchQuery.trim().length > 0 ? (
              <button
                type="button"
                onClick={() => onSearchChange("")}
                className="flex h-7 w-7 items-center justify-center rounded border border-border-subtle text-foreground-muted transition hover:border-brand hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                aria-label={t("market.search.clear")}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>
        {showStatusFilter && statusFilter && onStatusChange ? (
          <SegmentedFilter
            value={statusFilter}
            onChange={onStatusChange}
            ariaLabel="Regular season team status filter"
            className="w-full lg:w-[310px]"
            options={[
              { value: "ALL", label: t("market.filters.all") },
              { value: "ACTIVE", label: t("market.filters.active") },
              { value: "ELIMINATED", label: t("market.filters.eliminated"), activeTone: "sell" },
            ]}
          />
        ) : null}
      </div>
      {structure ? (
        <div className="mt-3 space-y-3 border-t border-border-subtle pt-3">
          <div>
            <div className="overflow-x-auto">
              <SegmentedFilter
                value={structure.conference}
                onChange={structure.onConferenceChange}
                ariaLabel={t("market.structureFilter.conference")}
                size="compact"
                className="w-full min-w-[260px]"
                options={(["ALL", ...structure.conferences] as MarketConferenceFilter[]).map((code) => ({
                  value: code,
                  label: code === "ALL"
                    ? t("market.structureFilter.all")
                    : code === "EAST" ? t("market.structureFilter.east") : code === "WEST" ? t("market.structureFilter.west") : code,
                }))}
              />
            </div>
          </div>
          <div>
            <div className="overflow-x-auto">
              <SegmentedFilter
                value={structure.division}
                onChange={structure.onDivisionChange}
                ariaLabel={t("market.structureFilter.division")}
                size="compact"
                className={structure.divisions.length > 4 ? "w-full min-w-[1000px]" : structure.divisions.length > 2 ? "w-full min-w-[600px]" : "w-full min-w-[330px]"}
                options={(["ALL", ...structure.divisions] as MarketDivisionFilter[]).map((code) => ({
                  value: code,
                  label: code === "ALL" ? t("market.structureFilter.all") : localizeStructureLabel(code, t),
                }))}
              />
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
